import { inject, injectable, named } from '@patsnap/tahiti-core/shared/inversify';
import psHttp from '@patsnap/common-ps-http';
import { I18nService } from '@analytics/shared/services/i18n.interface';
import {
    GlobalProxy,
    ModelContextType,
    type HostProps,
    type HiroEntryService,
    type HiroProps,
    type HiroBasicConfig,
    ContextProxy,
} from '@patsnap/hiro-event-proxy';
import { ParagraphHighlightService } from '@/services/paragraph-highlight-service.interface';
import { PatentBasicService } from '@/domains/patent/services/patent-basic-service';
import getParams from 'library-@/utils/get-params.js';
import { useRoute, useRouter } from 'vue-router/composables';
import { watch } from 'vue';
import { GetHiroBasic } from '@analytics/shared/apis/get-hiro-basic-api.interface';
import _ from 'lodash';
import { RavenInsertMeta } from 'library-@/services/sentry';
import { headerService } from '@/services/header-service';
import { TranslationSettingsService } from '@/domains/translation/services/translation-settings-service';
import { SettingType } from '@/domains/translation/enums';

@injectable()
export class HiroEntryServiceImpl implements HiroEntryService {
    private _hiroBasicConfig: HiroBasicConfig | null = null;

    constructor(
        @inject(I18nService)
        private readonly _i18n: I18nService,
        @inject(GlobalProxy)
        private readonly _globalProxy: GlobalProxy,
        @inject(ContextProxy)
        private readonly _contextProxy: ContextProxy,
        @inject(ParagraphHighlightService)
        private readonly _paragraphHighlightService: ParagraphHighlightService,
        @inject(PatentBasicService)
        private readonly _patentBasicService: PatentBasicService,
        @inject(GetHiroBasic)
        private readonly _getHiroBasic: GetHiroBasic,
        @inject(TranslationSettingsService)
        @named(SettingType.CURRENT)
        private readonly _currentTranslationSettingsService: TranslationSettingsService,
        @inject(TranslationSettingsService)
        @named(SettingType.EXPECTED)
        private readonly _expectedTranslationSettingsService: TranslationSettingsService,
    ) {
        this._globalProxy.eventProxy.on('loaded', () => {
            this._paragraphHighlightService.updateContextMenu();

            this._updateContext();
        });
        this._initWatchRoute();
        this._registerNavigateToDetail();
    }

    private _name = 'micro-hiro-pv';

    private _needFallback(enabledAgentCodes?: string[]) {
        enabledAgentCodes = enabledAgentCodes || this._hiroBasicConfig?.enabledAgentCodes;
        return !enabledAgentCodes?.includes?.('general_agent') && process.env.NODE_ENV !== 'development';
    }

    private _hiroBasicConfigPromise: Promise<HiroBasicConfig> | null = null;

    async getHiroBasicConfig() {
        if (this._hiroBasicConfigPromise) {
            return this._hiroBasicConfigPromise;
        }
        this._hiroBasicConfigPromise = this._getHiroBasic.getHiroBasic()
            .then(res => {
                const enabledAgentCodes = _.entries(res.agent_flag)
                    .filter(([_, value]) => value)
                    .map(([key]) => key);
                const baseUrl = `${process.env['VUE_APP_MF_HIRO']}`;
                const url = this._needFallback(enabledAgentCodes) ? `${baseUrl}/26.3.100` : baseUrl;

                this._hiroBasicConfig = {
                    name: this._name,
                    url,
                    enabled: !!res.copilot_flag,
                    enabledAgentCodes,
                    agentConfigs: res.agent_config ?? {},
                };
                return this._hiroBasicConfig;
            });
        return this._hiroBasicConfigPromise;
    }

    private _initWatchRoute() {
        const route = useRoute();
        watch(
            () => route.query.patentId,
            (newVal) => {
                this._globalProxy.rpcHiro.call('newChat');

                // 更新上下文
                this._updateContext();
            },
        );
    }

    private _registerNavigateToDetail() {
        const route = useRoute();
        const router = useRouter();

        this._globalProxy.rpcHost.register('navigateToDetail', async (params) => {
            const { patentId, tab, anchor } = params;

            this._currentTranslationSettingsService.setTranslationVisible({
                side: 'left',
                isVisible: false,
            });
            this._expectedTranslationSettingsService.setTranslationVisible({
                side: 'left',
                isVisible: false,
            });

            // 同一个专利，使用路由跳转
            const left = route.params.left as string;

            if (left) {
                router.push({
                    params: {
                        ...route.params,
                        left: tab,
                    },
                    query: {
                        ...route.query,
                        patentId,
                        anchor,
                    },
                });
            } else {
                router.push({
                    path: `/${tab}`,
                    query: {
                        ...route.query,
                        patentId,
                        anchor,
                    },
                });
            }
        });
    }

    private async _updateContext() {
        const urlParams: Record<string, string> = getParams();
        const patentId = urlParams.patentId;
        if (!patentId) return;
        const patent = await this._patentBasicService.getPatentBase(patentId);
        const currentPatentContext = {
            type: ModelContextType.PN,
            value: patent.PATENT_ID,
            label: patent.PN,
            hidden: false,
        };
        this._contextProxy.rpcHiro.call('patchContextState', {
            activeContexts: [currentPatentContext],
            currentContext: {
                label: this._i18n.t('currentPatent'),
                context: currentPatentContext,
            },
            // suggestionContext: {
            //     label: this._i18n.t('currentPatent'),
            //     contexts: [currentPatentContext],
            // },
        }, 'all');
    }

    public getHiroProps(): HiroProps {
        if (this._needFallback()) {
            return {
                defaultMenu: 'HiroChat',
                agentCodeScope: [
                    'autonomous_agent',
                ],
            };
        }
        return {
            defaultMenu: 'HiroAgentManager',
            defaultAgentCode: 'general_agent',
            agentCodeScope: [
                'general_agent',
            ],
        };
    }

    public getWujieProps(basicConfig: HiroBasicConfig): HostProps {
        return {
            name: this._name,
            locale: this._i18n.locale,
            messages: (this._i18n as any).messages,
            // @ts-ignore
            httpRequest: psHttp,
            proxy: {
                global: this._globalProxy,
                context: this._contextProxy,
            },
            enabledAgentCodes: basicConfig.enabledAgentCodes,
            agentConfigs: basicConfig.agentConfigs,
            tracker: {
                track: RavenInsertMeta,
            },
            headerService,
        };
    }
}
